import { HAZARDS_CONFIG, type HazardsConfig } from '../config/hazards.config';
import { WORLD_CONFIG } from '../config/world.config';
import { DynamicEventType } from '../dynamic-events/DynamicEventType';
import { ObstacleType } from '../obstacles/ObstacleType';
import { DEG_TO_RAD } from '../utils/math';
import {
  CraneBeamModel,
  CraneBlockModel,
  craneSwingArrivalPhase,
  craneSwingBox,
  craneSwingLateral,
  CraneSwingModel,
  craneSwingRadius,
  type CraneSwingPose,
} from './CraneLoadModels';
import { ExcavatorModel } from './ExcavatorModel';
import { HazardKind, VehicleType, type CraneSwingHazard, type HazardDefinition, type VehicleHazard } from './HazardDefinition';
import { colliderSlot } from './HazardModel';
import { vehicleSettings, VehicleModel, zigzagPose } from './VehicleModel';

/**
 * Static facts about hazard definitions, shared by the populator (rows for the sequence
 * validator, collectible placement) and the segment validator. Pure functions of the data.
 */

/** Dynamic event type the hazard's socket must support. */
export function hazardEventType(hazard: HazardDefinition): DynamicEventType {
  switch (hazard.kind) {
    case HazardKind.CraneBeam:
    case HazardKind.CraneBlock:
    case HazardKind.CraneSwing:
      return DynamicEventType.CraneLoadSwing;
    case HazardKind.Excavator:
      return DynamicEventType.ExcavatorSwing;
    case HazardKind.Vehicle:
      return hazard.vehicle === VehicleType.Truck ? DynamicEventType.SlowTruck : DynamicEventType.ForkliftCrossing;
  }
}

/** A vehicle rolling on an upper floor (VehicleHazard.elevation > 0) – outside the ground route's rules. */
export function isElevated(hazard: HazardDefinition): boolean {
  return hazard.kind === HazardKind.Vehicle && (hazard.elevation ?? 0) > 0;
}

/** Segment-local z of the line the hazard's timeline is keyed to (where it meets the runner). */
export function hazardKeyZ(hazard: HazardDefinition): number {
  return hazard.kind === HazardKind.Vehicle ? hazard.meetZ : hazard.z;
}

/** Seconds before the runner's arrival at the key line at which the warning starts. */
export function hazardStartLead(hazard: HazardDefinition, config: HazardsConfig): number {
  switch (hazard.kind) {
    case HazardKind.CraneBeam:
      return CraneBeamModel.startLead(config.craneBeam);
    case HazardKind.CraneBlock:
      return CraneBlockModel.startLead(config.craneBlock);
    case HazardKind.CraneSwing:
      return CraneSwingModel.startLead(config.craneSwing);
    case HazardKind.Excavator:
      return ExcavatorModel.startLead(config.excavator);
    case HazardKind.Vehicle:
      return VehicleModel.startLead(hazard, vehicleSettings(config, hazard.vehicle));
  }
}

/** Path the zig-zag is judged over around the key line (m before and after it) – where it can be when the runner arrives. */
const ZIGZAG_KEY_WINDOW = 1.5;

/**
 * Lanes a zig-zagging vehicle (the cable drum) touches around the key line – it bounces deterministically, so this is
 * where it is when it meets the runner (± a little path for the runner's timing). The validator keeps a lane free.
 */
function zigzagLanesAtKey(hazard: VehicleHazard, laneCount: number, config: HazardsConfig = HAZARDS_CONFIG): number[] {
  const s = vehicleSettings(config, hazard.vehicle);
  const world = WORLD_CONFIG;
  const atKey = hazard.startZ - (hazard.meetZ + s.front);
  const half = Math.max(...s.body.map((part) => Math.abs(part.center.x) + part.size.x / 2));
  const lanes = new Set<number>();
  for (let d = atKey - ZIGZAG_KEY_WINDOW; d <= atKey + ZIGZAG_KEY_WINDOW + 1e-6; d += 0.25) {
    const { x } = zigzagPose(hazard, s, world, Math.max(0, d));
    for (let lane = 0; lane < laneCount; lane++) {
      const center = (lane - (laneCount - 1) / 2) * world.laneWidth;
      if (Math.abs(center - x) < world.laneWidth / 2 + half) lanes.add(lane);
    }
  }
  return [...lanes].sort((a, b) => a - b);
}


/** Lanes a hazard can block at its key line. */
export function hazardLanes(hazard: HazardDefinition, laneCount: number): number[] {
  switch (hazard.kind) {
    case HazardKind.CraneBeam:
      return Array.from({ length: laneCount }, (_, lane) => lane);
    case HazardKind.Vehicle:
      if (hazard.zigzag) return zigzagLanesAtKey(hazard, laneCount);
      return hazard.laneChange ? [hazard.lane, hazard.laneChange.toLane] : [hazard.lane];
    default:
      return [hazard.lane];
  }
}

export interface HazardRequirement {
  readonly z: number;
  readonly lane: number;
  readonly type: ObstacleType;
}

/**
 * What the runner has to do at the hazard's key line – a decision row for the sequence
 * validator, conservative: beams = slide in every lane; block, bucket/heap and vehicles =
 * change lanes (both lanes of a vehicle that changes lanes); the swinging block = change out of the
 * lane it is over when the runner arrives (the validator checks it touches no other lane then).
 */
export function hazardRequirements(hazard: HazardDefinition, laneCount: number): HazardRequirement[] {
  if (isElevated(hazard)) return []; // on an upper floor – the ground route never meets it
  const type = hazard.kind === HazardKind.CraneBeam ? ObstacleType.Slide : ObstacleType.LaneChange;
  return hazardLanes(hazard, laneCount).map((lane) => ({ z: hazardKeyZ(hazard), lane, type }));
}

/** Stretch of one lane the hazard occupies (segment-local z range). */
export interface LaneStretch {
  readonly lane: number;
  readonly zFrom: number;
  readonly zTo: number;
}

/**
 * Where a vehicle drives through (its body from the stop position to the start), per lane.
 * Nothing static may stand there.
 */
export function vehiclePath(hazard: VehicleHazard, config: HazardsConfig): LaneStretch[] {
  const s = vehicleSettings(config, hazard.vehicle);
  // a zig-zag sweeps every lane along its whole path
  if (hazard.zigzag) {
    return Array.from({ length: WORLD_CONFIG.laneCount }, (_, lane) => ({ lane, zFrom: hazard.stopZ - s.front, zTo: hazard.startZ + s.rear }));
  }
  const change = hazard.laneChange;
  if (!change) return [{ lane: hazard.lane, zFrom: hazard.stopZ - s.front, zTo: hazard.startZ + s.rear }];
  return [
    { lane: hazard.lane, zFrom: change.atZ - s.laneChangeLength - s.front, zTo: hazard.startZ + s.rear },
    { lane: change.toLane, zFrom: hazard.stopZ - s.front, zTo: change.atZ + s.rear },
  ];
}

/**
 * Ground a hazard needs for itself – no static obstacle may stand there: a vehicle's path, the
 * spot where the block comes down, the rubble heap, and the stretches the swinging loads sweep.
 */
export function hazardOccupancy(hazard: HazardDefinition, config: HazardsConfig, laneCount: number): LaneStretch[] {
  if (isElevated(hazard)) return [];
  switch (hazard.kind) {
    case HazardKind.CraneBeam: {
      const { rig, swingDegrees } = config.craneBeam;
      const reach = rig.load.depth / 2 + (rig.pivotHeight - rig.loadBottom) * Math.sin((swingDegrees * Math.PI) / 180);
      return hazardLanes(hazard, laneCount).map((lane) => ({ lane, zFrom: hazard.z - reach, zTo: hazard.z + reach }));
    }
    case HazardKind.CraneBlock: {
      const half = config.craneBlock.rig.load.depth / 2;
      return [{ lane: hazard.lane, zFrom: hazard.z - half, zTo: hazard.z + half }];
    }
    case HazardKind.CraneSwing: {
      const reach = craneSwingAlongReach(config.craneSwing);
      return Array.from({ length: laneCount }, (_, lane) => ({ lane, zFrom: hazard.z - reach, zTo: hazard.z + reach }));
    }
    case HazardKind.Excavator: {
      const half = config.excavator.heap.depth / 2;
      return [{ lane: hazard.lane, zFrom: hazard.z - half, zTo: hazard.z + half }];
    }
    case HazardKind.Vehicle:
      return vehiclePath(hazard, config);
  }
}

/**
 * How a hazard affects the collectible line: `blocked` stretches get no bottles (they would sit
 * inside the load or the vehicle), `arc` stretches lift the bottles over the rubble heap.
 */
export interface CollectibleFootprint extends LaneStretch {
  readonly mode: 'blocked' | 'arc';
}

export function collectibleFootprints(hazard: HazardDefinition, config: HazardsConfig, clearance: number, laneCount = WORLD_CONFIG.laneCount): CollectibleFootprint[] {
  if (isElevated(hazard)) return [];
  switch (hazard.kind) {
    case HazardKind.CraneBeam:
      return []; // bottles under the beams = the risky slide route
    case HazardKind.CraneBlock: {
      const half = config.craneBlock.rig.load.depth / 2 + clearance;
      return [{ lane: hazard.lane, zFrom: hazard.z - half, zTo: hazard.z + half, mode: 'blocked' }];
    }
    case HazardKind.CraneSwing: {
      // The load sweeps every lane at hip height – no bottles inside its path.
      const half = craneSwingAlongReach(config.craneSwing) + clearance;
      return Array.from({ length: laneCount }, (_, lane) => ({ lane, zFrom: hazard.z - half, zTo: hazard.z + half, mode: 'blocked' as const }));
    }
    case HazardKind.Excavator: {
      const half = config.excavator.heap.depth / 2;
      return [{ lane: hazard.lane, zFrom: hazard.z - half, zTo: hazard.z + half, mode: 'arc' }];
    }
    case HazardKind.Vehicle:
      // Before the meeting line the runner gets there first – the risky route; behind it the vehicle drives through.
      return vehiclePath(hazard, config)
        .map((stretch) => ({ ...stretch, zFrom: Math.max(stretch.zFrom, hazard.meetZ - clearance), mode: 'blocked' as const }))
        .filter((stretch) => stretch.zTo > stretch.zFrom);
  }
}

// ── Swinging block ──────────────────────────────────────────────────────────

/** Time resolution of the lane scans over the swing (s) – the load moves ≤ 3 cm in it. */
const SWING_SCAN_STEP = 0.01;

/**
 * Farthest the swinging block reaches along the track from its key line: the wobble of the rope plus the
 * block's half diagonal (it turns freely while hoisted).
 */
export function craneSwingAlongReach(config: HazardsConfig['craneSwing']): number {
  const { load } = config.rig;
  const halfDiagonal = Math.hypot(load.width, load.height, load.depth) / 2;
  return craneSwingRadius(config.rig) * Math.sin(config.wobbleDegrees * DEG_TO_RAD) + halfDiagonal;
}

/**
 * Lanes the swinging block touches (a runner of `playerWidth` in the lane centre overlaps its collider)
 * while the swing clock runs from `from` to `to` (s relative to the runner's arrival) – full swing at hang
 * height, twisted to the full twist (the widest it gets across the track).
 */
export function craneSwingTouchedLanes(
  hazard: CraneSwingHazard,
  config: HazardsConfig['craneSwing'],
  laneCenters: readonly number[],
  playerWidth: number,
  from: number,
  to: number,
): number[] {
  const phase = craneSwingArrivalPhase(laneCenters[hazard.lane] ?? 0, hazard.sweep);
  const pose: CraneSwingPose = { hoist: config.hangClearance, swing: 0, spin: config.twistDegrees * DEG_TO_RAD, lateral: 0, pivotX: 0 };
  const slot = colliderSlot();
  const lateral = { angle: 0, pivotX: 0 };
  const touched = new Set<number>();
  const steps = Math.max(1, Math.ceil((to - from) / SWING_SCAN_STEP));
  for (let i = 0; i <= steps; i++) {
    craneSwingLateral(config, phase, from + ((to - from) * i) / steps, lateral);
    pose.lateral = lateral.angle;
    pose.pivotX = lateral.pivotX;
    const box = craneSwingBox(config.rig, hazard.z, pose, slot).box;
    laneCenters.forEach((x, lane) => {
      if (box.minX < x + playerWidth / 2 && box.maxX > x - playerWidth / 2) touched.add(lane);
    });
  }
  return [...touched].sort((a, b) => a - b);
}
